import { useState, type ReactNode } from 'react';
import { legsValid } from './leg_state.js';
import type { SearchSelectOption } from './place_option.js';
import { NARROW_STAGE_QUERY } from '../../shared/narrow_stage.js';
import { useFocusCtaOnComplete, useGuidedFill } from './guided_fill.js';
import { useRememberedPrices } from './heat_savings.js';
import { MultiCta, MultiReset } from './planner_ctas.js';
import { localDayIso } from './planner_dates.js';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import { legsRangeLabel, plannerLabels } from './planner_labels.js';
import {
  recentSection,
  type PlannerFormProps,
  type PlannerBodyProps,
} from './planner_form_model.js';
import {
  usePlannerState,
  useDraftGate,
  type LegPager,
  useWhenPopover,
  usePlannerSnapshots,
  useTripTypeSelection,
  resetPlanner,
} from './planner_state.js';
import { useBinaryPlanner } from './binary_planner.js';
import { useMultiPlanner } from './multi_planner.js';
import { RouteRowView, WhoField, LegBuilderView, LegsFieldView } from './planner_fields.js';
import { LegCalendarView } from './planner_calendars.js';
import { PlannerLayoutView, inlineCtas } from './planner_layout.js';
import { LegWhenField, BinaryWhenView } from './planner_when_fields.js';
import './planner_card.css';
import './flight_planner.css';

/**
 * Meses lado a lado na grade — que agora abre SEMPRE num popover (#2136: as
 * datas saíram do corpo do card em todas as formas, e o card mede o mesmo nas
 * três abas). Dois é o teto de dropdown do desenho (decisão de 2026-08-04:
 * *"nos cenários em que os calendários são exibidos como dropdowns eles devem
 * ter largura de no máximo 2 meses"*) — camada flutuante mais larga deixa de
 * ser controle de campo e passa a cobrir o conteúdo que a pessoa consulta pra
 * escolher a data.
 */
const POPOVER_MONTHS_VISIBLE = 2;

/** Nenhum aeroporto: default estável de `searchAirports` para o host que não tem
 *  seam de busca de praça (preview/storybook sem port) — o combobox degrada pra
 *  "Nenhum aeroporto". Módulo (não arrow inline) porque a identidade da fn é dep do
 *  effect de busca do PlaceField. */
const NO_AIRPORTS = () => Promise.resolve<SearchSelectOption[]>([]);

function PlannerBody(props: PlannerBodyProps) {
  const { state, binary, multi, initial, searchAirports } = props;
  const { band, collapsed, readOnly, loading } = props;
  const { originSections, destinationSections } = props;
  const { ctaLabel, note, copy, locale, openLegs, restoreLegs } = props;
  const { submitDates, selectTripType, onReset, onCancel, hideWho, floatDatesPopover } = props;
  // The guide's ref apart from the guide: the ref only goes to the Buscar.
  const { guide } = props;
  const { ctaRef } = guide;
  const isMulti = binary.showTabs && state.activeTripType === 'multi_leg';
  // O layout ABERTO (#2136): datas em popover controlado, saia só no intervalo,
  // linhas do multi sempre abertas com o Quando por linha em popover.
  const openLayout = !band && !collapsed;
  /** O palco de FOLHA: estreito E no layout aberto. Fora dele os campos seguem
   *  em popover, então quem apaga affordance por "estar na folha" precisa desta
   *  conta, não só de `narrow`. Uma const só (e não a conjunção repetida em cada
   *  prop) mantém a régua única e a complexidade desta função no teto. */
  const narrowSheet = props.narrow && openLayout;
  const legsBuilder = (cta?: ReactNode) => (
    <LegBuilderView
      state={state}
      multi={multi}
      band={band}
      collapsed={collapsed}
      readOnly={readOnly}
      searchAirports={searchAirports}
      originSections={originSections}
      destinationSections={destinationSections}
      {...(cta ? { cta } : {})}
      {...(openLayout && !readOnly ? { legDateField } : {})}
      placeSheet={narrowSheet}
      stacked={narrowSheet}
      guide={guide}
      copy={copy}
      locale={locale}
      airportLabel={props.airportLabel}
    />
  );
  // A saída do multi é a MESMA nas duas réguas; só o tamanho (e o `ref` do guia,
  // no rodapé do card) muda.
  const multiSearch = guide.searchCta(true, legsValid(state.legs), multi.submitMulti);
  const multiCtaProps = { ...multiSearch, ctaLabel };
  const multiCta = readOnly ? null : <MultiCta large={false} {...multiCtaProps} />;
  // NAKED (#2167) / inlineSearch (#2207): a moldura sai mesmo congelado; os CTAs
  // inline, como os do card, só existem fora do readOnly (a conta mora no helper,
  // pelo teto de complexidade desta função).
  const inlineUi = inlineCtas(props);
  /* A MESMA saída em régua de rodapé (`lg`, 36px): no CARD a saia é uma só nas
     três abas (UX Review Pacote 2, M2); a cápsula sm acima segue com a FAIXA. */
  const multiFooterCta = readOnly ? null : (
    <>
      <MultiReset onReset={onReset} copy={copy} large />
      <MultiCta ref={ctaRef} large {...multiCtaProps} />
    </>
  );
  const legsCalendar = (
    <LegCalendarView
      state={state}
      multi={multi}
      readOnly={readOnly}
      loading={loading}
      // MESMA conta de `BinaryWhenView`: as setas só saem do rolo quando existe
      // a barra pregada que as substitui, e ela é do `legDateField` — que o
      // `PlannerBody` só monta no layout ABERTO. No band/collapsed o calendário
      // mora no popover da `PlannerLegsField`, sem barra: só `props.narrow` ali
      // apagava os pagers e deixava a perna em edição sem navegação de mês.
      sheet={narrowSheet}
      copy={copy}
      locale={locale}
    />
  );
  // Ver `LegWhenField` — o QUANDO por linha do multi, em popover sem saia.
  const legDateField = (index: number): ReactNode => (
    <LegWhenField
      state={state}
      multi={multi}
      index={index}
      calendar={legsCalendar}
      floatDatesPopover={floatDatesPopover}
      sheet={props.narrow}
      cityOfAirport={props.cityOfAirport}
      copy={copy}
      locale={locale}
    />
  );
  const whenField = <BinaryWhenView props={props} openLayout={openLayout} />;
  const routeRow = (
    <RouteRowView
      state={state}
      binary={binary}
      whenField={whenField}
      band={band}
      readOnly={readOnly}
      searchAirports={searchAirports}
      originSections={originSections}
      destinationSections={destinationSections}
      ctaLabel={ctaLabel}
      sheet={props.narrow && !band}
      submitDates={submitDates}
      cta={inlineUi.row}
      guide={guide}
      copy={copy}
      airportLabel={props.airportLabel}
    />
  );
  const legsField = (
    <LegsFieldView
      state={state}
      {...(legsRangeLabel(state.legs, locale) ? { range: legsRangeLabel(state.legs, locale) } : {})}
      {...(note ? { note } : {})}
      readOnly={readOnly}
      band={band}
      cta={multiCta}
      builder={legsBuilder()}
      calendar={legsCalendar}
      openLegs={openLegs}
      restoreLegs={restoreLegs}
      submitMulti={multi.submitMulti}
      copy={copy}
    />
  );
  return (
    <PlannerLayoutView
      band={band}
      collapsed={collapsed}
      naked={inlineUi.naked}
      inlineSearch={inlineUi.inlineSearch}
      showTabs={binary.showTabs}
      isMulti={isMulti}
      activeTripType={state.activeTripType}
      readOnly={readOnly}
      selectTripType={selectTripType}
      legsField={legsField}
      routeRow={routeRow}
      whenField={whenField}
      whoRow={
        hideWho ? null : (
          // A linha do QUEM, a mesma nos dois modos: a coluna veste a anatomia da
          // fileira (`.fpl-filter`, rótulo em cima) e a pílula mede o conteúdo —
          // avatar + nome, como no frame.
          <div className="fpl-filter">
            <WhoField
              state={state}
              initial={initial}
              readOnly={readOnly}
              sheet={narrowSheet}
              directory={props.travelerDirectory}
              copy={copy}
            />
          </div>
        )
      }
      legsBuilder={legsBuilder(inlineUi.multiCta)}
      multiFooterCta={multiFooterCta}
      {...(note ? { note } : {})}
      search={guide.searchCta(false, binary.ready, submitDates)}
      ctaLabel={ctaLabel}
      ctaRef={ctaRef}
      {...(onCancel ? { onCancel } : {})}
      copy={copy}
    />
  );
}

export function PlannerForm({
  initial,
  heat,
  heatLoading = false,
  readOnly = false,
  note,
  submitLabel,
  layout = 'open',
  searchAirports = NO_AIRPORTS,
  fetchHeat,
  autoFetchHeat = true,
  onSubmit,
  onDirtyChange,
  onCancel,
  hideWho = false,
  // Sem default (`= false` contaria como branch — o form está no teto de
  // complexidade): quem resolve o falsy é `inlineCtas`/`searchCtaLabel`.
  naked,
  // Sem default, pelo mesmo motivo de `naked` (um default conta como branch e o
  // form está no teto de complexidade): quem resolve o falsy é `inlineCtas`.
  inlineSearch,
  initialDatesOpen = false,
  // Sem default aqui: `PlannerForm` já está no teto de complexidade, e o
  // default (`false`) é resolvido mais abaixo, em `WhenFieldView`/`LegWhenField`
  // (que têm orçamento livre) — repassar `undefined` até lá não muda nada.
  floatDatesPopover,
  // Sem default pela mesma razão: quem resolve o falsy é `useGuidedFill`.
  guided,
  guidedOpen: opens,
  cityOfAirport,
  airportLabel,
  travelerDirectory,
  recentAirports,
  copy,
  locale,
}: Readonly<PlannerFormProps>) {
  const collapsed = layout === 'collapsed';
  const band = layout === 'band';
  // As datas moram SEMPRE numa camada desde o #2136 (o card mede o mesmo nas
  // três abas) — o teto de dropdown vale em todo degrau de layout; no estreito
  // a camada é a folha, com os mesmos dois meses empilhados. O palco estreito é a
  // régua `sm` do DS, a MESMA do bloco `@media` de `flight_planner.css` que empilha
  // a fileira QUEM · DE · PARA · QUANDO: os dois viram juntos.
  const monthsVisible = POPOVER_MONTHS_VISIBLE;
  const narrow = useMediaQuery(NARROW_STAGE_QUERY);
  // Histórico do viajante (do host): o combobox DE/PARA abre OFERECENDO em vez de
  // pedir. Sem histórico ⇒ `undefined` nas duas pontas e nada muda.
  const recent = copy.airportRecent;
  const originSections = recentSection(recentAirports, 'origin', recent);
  const destinationSections = recentSection(recentAirports, 'destination', recent);
  const todayIso = localDayIso();
  const openLayout = !band && !collapsed;
  const state = usePlannerState(initial, todayIso, initialDatesOpen && openLayout);
  const { origin, destination, activeTripType, legs, out, ret } = state;
  const { whenOpen, setWhenOpen } = useWhenPopover(openLayout, initialDatesOpen, initial);
  const guide = useGuidedFill({ guided, openLayout, readOnly, opens, initial, state, setWhenOpen });
  const rebaseDraft = useDraftGate(
    { tripType: activeTripType, origin, destination, out, ret, legs },
    onDirtyChange,
  );
  const [legPager, setLegPager] = useState<LegPager>({ legId: null, fromMonth: null });
  const binary = useBinaryPlanner({
    state,
    initial,
    heat,
    heatLoading,
    readOnly,
    autoFetchHeat,
    fetchHeat,
    onSubmit,
    monthsVisible,
    todayIso,
    afterPick: guide.afterRoutePick,
  });
  const { showTabs, echoFields, search } = binary;
  const isMulti = showTabs && activeTripType === 'multi_leg';
  useFocusCtaOnComplete(guide, whenOpen, binary.ready);

  const resetLegPager = () => {
    setLegPager({ legId: null, fromMonth: null });
  };
  const selectTripType = useTripTypeSelection(state, initial, resetLegPager);
  const onReset = () => {
    resetPlanner(state, resetLegPager);
  };

  const multi = useMultiPlanner({
    state,
    heat,
    isMulti,
    readOnly,
    heatLoading,
    autoFetchHeat,
    fetchHeat,
    onSubmit,
    echoFields,
    rebaseDraft,
    legPager,
    setLegPager,
    monthsVisible,
    todayIso,
    guide,
  });
  const { choice, best, ctaLabel, confirmDatesLabel, whenLabel, whenPendingTail } = plannerLabels({
    state,
    binary,
    heat: useRememberedPrices(heat, state.travelers.length, initial.passengers),
    ...(submitLabel ? { submitLabel } : {}),
    band,
    collapsed,
    naked,
    copy,
    locale,
  });

  const { openDates, restoreDates, openLegs, restoreLegs } = usePlannerSnapshots(state);
  // O echo da busca binária. Serve o CTA da FAIXA e o primário do popover do
  // colapsado — o mesmo gesto, portas diferentes.
  const submitDates = () => {
    rebaseDraft();
    if (out) search(out, ret);
  };
  return (
    <PlannerBody
      state={state}
      binary={binary}
      multi={multi}
      band={band}
      collapsed={collapsed}
      naked={naked}
      inlineSearch={inlineSearch}
      readOnly={readOnly}
      initial={initial}
      searchAirports={searchAirports}
      originSections={originSections}
      destinationSections={destinationSections}
      ctaLabel={ctaLabel}
      confirmDatesLabel={confirmDatesLabel}
      loading={heatLoading}
      todayIso={todayIso}
      choice={choice}
      {...(best ? { best } : {})}
      {...(note ? { note } : {})}
      whenLabel={whenLabel}
      whenPendingTail={whenPendingTail}
      whenOpen={whenOpen}
      onWhenOpenChange={setWhenOpen}
      openDates={openDates}
      restoreDates={restoreDates}
      openLegs={openLegs}
      restoreLegs={restoreLegs}
      submitDates={submitDates}
      selectTripType={selectTripType}
      onReset={onReset}
      {...(onCancel ? { onCancel } : {})}
      hideWho={hideWho}
      floatDatesPopover={floatDatesPopover}
      narrow={narrow}
      cityOfAirport={cityOfAirport}
      airportLabel={airportLabel}
      travelerDirectory={travelerDirectory}
      guide={guide}
      copy={copy}
      locale={locale}
    />
  );
}
